import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ChevronLeft, ChevronRight, Check, Camera, Home, Briefcase, CalendarDays, AlertTriangle, RefreshCw, User
} from 'lucide-react';
import { toast } from 'sonner';

const ATTENDANCE_TYPES = [
  { value: 'present', label: 'Present (Office)', icon: Check, color: 'bg-emerald-500', mode: 'office' },
  { value: 'on_shoot', label: 'On Shoot', icon: Camera, color: 'bg-blue-500', mode: 'shoot' },
  { value: 'wfh', label: 'Work From Home', icon: Home, color: 'bg-purple-500', mode: 'office' },
  { value: 'leave', label: 'Leave', icon: Briefcase, color: 'bg-rose-500', mode: 'office' }
];

const LEAVE_TYPES = [
  { value: 'leave', label: 'Leave', limit: 4 },
  { value: 'shoot_day', label: 'Shoot Day', limit: 'Unlimited' }
];

const AttendancePage = () => {
  const { user } = useAuth();
  const [attendance, setAttendance] = useState([]);
  const [teamAttendance, setTeamAttendance] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showMarkDialog, setShowMarkDialog] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [showPauseDialog, setShowPauseDialog] = useState(false);
  const [projectsToPause, setProjectsToPause] = useState([]);
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);
  const [markAttendance, setMarkAttendance] = useState({ status: 'present', mode: 'office', notes: '' });
  const [leaveRequest, setLeaveRequest] = useState({
    start_date: '',
    end_date: '',
    leave_type: 'leave',
    reason: ''
  });
  const [selectedTeamMember, setSelectedTeamMember] = useState('self');
  const [teamMembers, setTeamMembers] = useState([]);

  const isManager = (user?.roles || [user?.role]).some(r => ['admin', 'manager'].includes(r));

  const viewingOther = isManager && selectedTeamMember && selectedTeamMember !== 'self';
  const selectedMember = viewingOther ? teamMembers.find(m => m.id === selectedTeamMember) : null;
  const monthRecords = viewingOther
    ? teamAttendance.filter(a => a.user_id === selectedTeamMember)
    : attendance.filter(a => !a.user_id || a.user_id === user?.id);

  useEffect(() => {
    fetchData();
  }, [currentMonth, selectedTeamMember]);

  const fetchData = async () => {
    try {
      const month = currentMonth.getMonth() + 1;
      const year = currentMonth.getFullYear();
      
      const promises = [
        api.get(`/attendance?month=${month}&year=${year}`),
        api.get('/leave-requests'),
        api.get(`/leave-balance/${user?.id}`)
      ];
      
      if (isManager) {
        promises.push(api.get(`/attendance/team?month=${month}&year=${year}`));
        promises.push(api.get('/users/team'));
      }
      
      const results = await Promise.all(promises);
      setAttendance(results[0].data);
      setLeaveRequests(results[1].data);
      setLeaveBalance(results[2].data);
      
      if (isManager) {
        setTeamAttendance(results[3].data);
        setTeamMembers(results[4].data);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAttendance = async () => {
    try {
      const todayLocal = new Date();
      const ymd = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
      if (ymd(selectedDate) > ymd(todayLocal)) {
        toast.error('Cannot mark attendance for a future date');
        return;
      }
      const selectedType = ATTENDANCE_TYPES.find(t => t.value === markAttendance.status);
      // Send local Y-M-D so the stored date matches the day the user clicked (avoids UTC off-by-one in IST)
      const y = selectedDate.getFullYear();
      const m = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const d = String(selectedDate.getDate()).padStart(2, '0');
      const localDateISO = `${y}-${m}-${d}T00:00:00.000Z`;
      const result = await api.post('/attendance', {
        date: localDateISO,
        status: markAttendance.status,
        mode: selectedType?.mode || markAttendance.mode || 'office',
        notes: markAttendance.notes
      });
      
      const lateMsg = result.data.late_status === 'late' ? ' (Late arrival)' :
                       result.data.late_status === 'half_day' ? ' (Half day - late)' : '';
      toast.success('Attendance marked' + lateMsg);
      
      // Check if we should show pause projects dialog
      if (result.data.offer_pause && result.data.on_shoot_projects?.length > 0) {
        setProjectsToPause(result.data.on_shoot_projects);
        setShowMarkDialog(false);
        setShowPauseDialog(true);
      } else {
        setShowMarkDialog(false);
        setMarkAttendance({ status: 'present', mode: 'office', notes: '' });
        fetchData();
      }
    } catch (err) {
      toast.error('Failed to mark attendance');
    }
  };

  const handlePauseProjects = async () => {
    try {
      if (selectedProjectIds.length > 0) {
        await api.post('/attendance/pause-projects', selectedProjectIds);
        toast.success(`${selectedProjectIds.length} projects paused for shoot`);
      }
      setShowPauseDialog(false);
      setProjectsToPause([]);
      setSelectedProjectIds([]);
      setMarkAttendance({ status: 'present', mode: 'office', notes: '' });
      fetchData();
    } catch (err) {
      toast.error('Failed to pause projects');
    }
  };

  const handleLeaveRequest = async () => {
    // Check balance before submitting
    if (leaveRequest.leave_type !== 'shoot_day' && leaveBalance) {
      const daysRequested = Math.ceil(
        (new Date(leaveRequest.end_date) - new Date(leaveRequest.start_date)) / (1000 * 60 * 60 * 24)
      ) + 1;
      const remaining = leaveBalance.leave?.remaining || 0;
      
      if (daysRequested > remaining) {
        toast.error(`Insufficient leave balance this month. You have ${remaining} day(s) remaining.`);
        return;
      }
    }
    
    try {
      await api.post('/leave-requests', {
        start_date: new Date(leaveRequest.start_date).toISOString(),
        end_date: new Date(leaveRequest.end_date).toISOString(),
        leave_type: leaveRequest.leave_type,
        reason: leaveRequest.reason
      });
      
      toast.success('Leave request submitted');
      setShowLeaveDialog(false);
      setLeaveRequest({ start_date: '', end_date: '', leave_type: 'leave', reason: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit leave request');
    }
  };

  const handleLeaveAction = async (leaveId, status) => {
    try {
      await api.patch(`/leave-requests/${leaveId}?status=${status}`);
      toast.success(`Leave ${status}`);
      fetchData();
    } catch (err) {
      toast.error(`Failed to ${status} leave`);
    }
  };

  const getAttendanceForDate = (date, userId = null) => {
    // Compute local date string to avoid UTC off-by-one when browser is in IST
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const dateStr = `${y}-${m}-${d}`;
    const records = userId && isManager ? teamAttendance : attendance;
    return records.find(a => {
      const ad = new Date(a.date);
      const ay = ad.getFullYear();
      const am = String(ad.getMonth() + 1).padStart(2, '0');
      const add = String(ad.getDate()).padStart(2, '0');
      const aStr = `${ay}-${am}-${add}`;
      return aStr === dateStr && (!userId || a.user_id === userId);
    });
  };

  const getAttendanceColor = (status) => {
    const type = ATTENDANCE_TYPES.find(t => t.value === status);
    return type?.color || 'bg-zinc-500';
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
      approved: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      rejected: 'bg-rose-500/10 text-rose-400 border-rose-500/20'
    };
    return badges[status] || badges.pending;
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Calculate stats — reflects whoever is currently being viewed
  const stats = {
    present: monthRecords.filter(a => a.status === 'present').length,
    on_shoot: monthRecords.filter(a => a.status === 'on_shoot').length,
    wfh: monthRecords.filter(a => a.status === 'wfh').length,
    leave: monthRecords.filter(a => a.status === 'leave').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F5A623]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="attendance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-white tracking-tight">Attendance</h1>
          <p className="text-zinc-500 text-sm mt-1">
            {viewingOther
              ? `Viewing ${selectedMember?.name || 'team member'}'s attendance`
              : 'Track your daily attendance and leaves'}
          </p>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          {isManager && (
            <Select value={selectedTeamMember} onValueChange={setSelectedTeamMember}>
              <SelectTrigger className="w-52 bg-[#0A0A0A] border-white/10 text-white h-9" data-testid="attendance-team-select">
                <SelectValue placeholder="My Calendar" />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10 max-h-[320px]">
                <SelectItem value="self" className="text-white hover:bg-white/10">My Calendar</SelectItem>
                {teamMembers.map(m => (
                  <SelectItem key={m.id} value={m.id} className="text-white hover:bg-white/10">
                    {m.name} <span className="text-zinc-500 text-xs">· {m.role}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" size="sm" onClick={fetchData}
            className="border-[#F5A623]/30 text-[#F5A623] hover:bg-[#F5A623]/10" data-testid="refresh-attendance">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
          </Button>
          {!viewingOther && (
            <>
              <Button 
                onClick={() => {
                  setSelectedDate(new Date());
                  setShowMarkDialog(true);
                }}
                className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black"
                data-testid="mark-attendance-btn"
              >
                <Check className="h-4 w-4 mr-2" /> Mark Today
              </Button>
              <Button 
                variant="outline"
                onClick={() => setShowLeaveDialog(true)}
                className="border-white/10 text-white hover:bg-white/5 hover:text-white"
                data-testid="request-leave-btn"
              >
                <CalendarDays className="h-4 w-4 mr-2" /> Request Leave
              </Button>
            </>
          )}
        </div>
      </div>

      {viewingOther && (
        <div className="flex items-center justify-between px-4 py-2.5 bg-[#F5A623]/10 border border-[#F5A623]/30 rounded-lg" data-testid="viewing-banner">
          <div className="flex items-center gap-2 text-sm">
            <User className="h-4 w-4 text-[#F5A623]" />
            <span className="text-white">Viewing <strong className="text-[#F5A623]">{selectedMember?.name}</strong>{"'s"} attendance</span>
            <span className="text-zinc-500 text-xs">· {monthRecords.length} entries this month</span>
          </div>
          <Button size="sm" variant="ghost" onClick={() => setSelectedTeamMember('self')} className="text-zinc-400 hover:text-white h-7 text-xs">
            Back to My Calendar
          </Button>
        </div>
      )}

      {/* Stats + Leave Balance */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        {ATTENDANCE_TYPES.map(type => (
          <Card key={type.value} className="bg-[#121212] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 ${type.color}/10 rounded`}>
                  <type.icon className={`h-4 w-4 ${type.color.replace('bg-', 'text-')}`} />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-white">{stats[type.value] || 0}</p>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">{type.label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        
        {/* Leave Balance Cards */}
        {leaveBalance && (
          <>
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="p-4">
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Leave (this month)</p>
                  <p className="text-lg font-semibold text-white">
                    {leaveBalance.leave?.remaining ?? 4}/{leaveBalance.leave?.total ?? 4}
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="p-4">
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Shoot Days</p>
                  <p className="text-lg font-semibold text-white">{leaveBalance.shoot_day?.used ?? 0} taken</p>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <div className="lg:col-span-2">
          <Card className="bg-[#121212] border-white/10">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">
                  {currentMonth.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
                </CardTitle>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={prevMonth}
                    className="h-8 w-8 border-white/10 text-white hover:bg-white/5"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={nextMonth}
                    className="h-8 w-8 border-white/10 text-white hover:bg-white/5"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-1 text-center mb-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="text-xs text-zinc-500 uppercase tracking-wider py-2">
                    {day}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {(() => {
                  const days = [];
                  const year = currentMonth.getFullYear();
                  const month = currentMonth.getMonth();
                  const firstDay = new Date(year, month, 1).getDay();
                  const daysInMonth = new Date(year, month + 1, 0).getDate();
                  
                  for (let i = 0; i < firstDay; i++) {
                    days.push(<div key={`empty-${i}`} className="p-2" />);
                  }
                  
                  for (let day = 1; day <= daysInMonth; day++) {
                    const date = new Date(year, month, day);
                    const viewingOther = selectedTeamMember && selectedTeamMember !== 'self';
                    const attendanceRecord = getAttendanceForDate(date, viewingOther ? selectedTeamMember : null);
                    const isToday = new Date().toDateString() === date.toDateString();
                    
                    days.push(
                      <button
                        key={day}
                        onClick={() => {
                          if (!viewingOther) {
                            setSelectedDate(date);
                            setShowMarkDialog(true);
                          }
                        }}
                        disabled={viewingOther}
                        className={`p-2 rounded text-sm transition-colors relative ${
                          isToday ? 'ring-1 ring-[#F5A623]' : ''
                        } ${attendanceRecord ? 'hover:opacity-80' : 'hover:bg-white/5'} ${viewingOther ? 'cursor-default' : ''}`}
                        data-testid={`calendar-day-${day}`}
                      >
                        <span className={attendanceRecord ? 'text-white' : 'text-zinc-400'}>
                          {day}
                        </span>
                        {attendanceRecord && (
                          <div className={`absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full ${getAttendanceColor(attendanceRecord.status)}`} />
                        )}
                      </button>
                    );
                  }
                  
                  return days;
                })()}
              </div>
              
              <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-white/10">
                {ATTENDANCE_TYPES.map(type => (
                  <div key={type.value} className="flex items-center gap-2 text-xs text-zinc-400">
                    <div className={`w-2 h-2 rounded-full ${type.color}`} />
                    <span>{type.label}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Leave Requests */}
        <div>
          <Card className="bg-[#121212] border-white/10">
            <CardHeader className="pb-3">
              <CardTitle className="text-white text-lg">Leave Requests</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 max-h-[500px] overflow-y-auto">
              {leaveRequests.length === 0 ? (
                <div className="text-center py-8 text-zinc-500">
                  <CalendarDays className="h-10 w-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No leave requests</p>
                </div>
              ) : (
                leaveRequests.map(leave => (
                  <div key={leave.id} className="p-3 bg-[#0A0A0A] border border-white/5 rounded">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div>
                        <p className="text-white text-sm font-medium capitalize">
                          {leave.leave_type.replace('_', ' ')} Leave
                        </p>
                        <p className="text-xs text-zinc-500">
                          {formatDate(leave.start_date)} - {formatDate(leave.end_date)}
                        </p>
                        {isManager && leave.user_id !== user?.id && (
                          <p className="text-xs text-zinc-400 mt-1">{leave.user_name}</p>
                        )}
                      </div>
                      <Badge variant="outline" className={getStatusBadge(leave.status)}>
                        {leave.status}
                      </Badge>
                    </div>
                    <p className="text-xs text-zinc-400">{leave.reason}</p>
                    
                    {isManager && leave.status === 'pending' && leave.user_id !== user?.id && (
                      <div className="flex gap-2 mt-3">
                        <Button 
                          size="sm" 
                          onClick={() => handleLeaveAction(leave.id, 'approved')}
                          className="flex-1 bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 text-xs h-7"
                          data-testid={`approve-leave-${leave.id}`}
                        >
                          Approve
                        </Button>
                        <Button 
                          size="sm" 
                          onClick={() => handleLeaveAction(leave.id, 'rejected')}
                          className="flex-1 bg-rose-500/20 text-rose-400 hover:bg-rose-500/30 text-xs h-7"
                          data-testid={`reject-leave-${leave.id}`}
                        >
                          Reject
                        </Button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Team Overview — managers only */}
      {isManager && !viewingOther && teamMembers.length > 0 && (
        <Card className="bg-[#121212] border-white/10" data-testid="team-overview-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white text-lg">
                Team Overview — {currentMonth.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
              </CardTitle>
              <span className="text-xs text-zinc-500">Click a row to view calendar</span>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left text-xs text-zinc-400 font-medium px-4 py-2">Member</th>
                    <th className="text-center text-xs text-zinc-400 font-medium px-3 py-2">Present</th>
                    <th className="text-center text-xs text-zinc-400 font-medium px-3 py-2">On Shoot</th>
                    <th className="text-center text-xs text-zinc-400 font-medium px-3 py-2">WFH</th>
                    <th className="text-center text-xs text-zinc-400 font-medium px-3 py-2">Leave</th>
                    <th className="text-center text-xs text-zinc-400 font-medium px-3 py-2">Late</th>
                    <th className="text-center text-xs text-zinc-400 font-medium px-3 py-2">Total Days</th>
                  </tr>
                </thead>
                <tbody>
                  {teamMembers.map(m => {
                    const mRecs = teamAttendance.filter(a => a.user_id === m.id);
                    const c = (s) => mRecs.filter(r => r.status === s).length;
                    const late = mRecs.filter(r => r.late_status === 'late' || r.late_status === 'half_day').length;
                    return (
                      <tr key={m.id}
                        onClick={() => setSelectedTeamMember(m.id)}
                        className="border-b border-white/5 hover:bg-white/[0.03] cursor-pointer"
                        data-testid={`team-row-${m.id}`}>
                        <td className="px-4 py-2.5">
                          <p className="text-white text-sm font-medium">{m.name}</p>
                          <p className="text-[10px] text-zinc-500 capitalize">{(m.roles || [m.role]).join(', ').replace(/_/g, ' ')}</p>
                        </td>
                        <td className="text-center text-sm text-emerald-400 px-3">{c('present')}</td>
                        <td className="text-center text-sm text-blue-400 px-3">{c('on_shoot')}</td>
                        <td className="text-center text-sm text-purple-400 px-3">{c('wfh')}</td>
                        <td className="text-center text-sm text-rose-400 px-3">{c('leave')}</td>
                        <td className="text-center text-sm text-amber-400 px-3">{late > 0 ? late : '—'}</td>
                        <td className="text-center text-sm text-white px-3 font-medium">{mRecs.length}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Mark Attendance Dialog */}
      <Dialog open={showMarkDialog} onOpenChange={setShowMarkDialog}>
        <DialogContent className="bg-[#121212] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Mark Attendance</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="text-center p-3 bg-[#0A0A0A] rounded border border-white/5">
              <p className="text-zinc-400 text-sm">Selected Date</p>
              <p className="text-white text-lg font-medium">
                {selectedDate.toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
              </p>
            </div>
            
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Status</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                {ATTENDANCE_TYPES.map(type => (
                  <button
                    key={type.value}
                    onClick={() => setMarkAttendance({...markAttendance, status: type.value})}
                    className={`p-3 rounded border transition-colors ${
                      markAttendance.status === type.value 
                        ? `${type.color} border-current text-white` 
                        : 'border-white/10 text-zinc-400 hover:border-white/20'
                    }`}
                    data-testid={`attendance-type-${type.value}`}
                  >
                    <type.icon className="h-5 w-5 mx-auto mb-1" />
                    <span className="text-xs">{type.label}</span>
                  </button>
                ))}
              </div>
            </div>
            
            {markAttendance.status === 'on_shoot' && (
              <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-blue-400 mt-0.5" />
                  <p className="text-xs text-blue-300">
                    If you have active projects, you'll be prompted to pause them after marking attendance.
                  </p>
                </div>
              </div>
            )}
            
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Notes (Optional)</Label>
              <Textarea
                value={markAttendance.notes}
                onChange={(e) => setMarkAttendance({...markAttendance, notes: e.target.value})}
                className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                placeholder="Any additional notes..."
                rows={2}
              />
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowMarkDialog(false)} className="border-white/10 text-white hover:bg-white/5">
                Cancel
              </Button>
              <Button onClick={handleMarkAttendance} className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black" data-testid="submit-attendance-btn">
                Mark Attendance
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pause Projects Dialog */}
      <Dialog open={showPauseDialog} onOpenChange={setShowPauseDialog}>
        <DialogContent className="bg-[#121212] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Pause Active Projects?</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-zinc-400">
              You have {projectsToPause.length} active project(s). Would you like to pause them while you're on shoot?
            </p>
            
            <div className="space-y-2">
              {projectsToPause.map(project => (
                <div 
                  key={project.id} 
                  className="flex items-center gap-3 p-3 bg-[#0A0A0A] border border-white/5 rounded"
                >
                  <Checkbox
                    id={project.id}
                    checked={selectedProjectIds.includes(project.id)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedProjectIds([...selectedProjectIds, project.id]);
                      } else {
                        setSelectedProjectIds(selectedProjectIds.filter(id => id !== project.id));
                      }
                    }}
                    className="border-white/20"
                  />
                  <div className="flex-1">
                    <p className="text-white text-sm">{project.title}</p>
                    <p className="text-xs text-zinc-500">{project.client_name}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowPauseDialog(false);
                  setProjectsToPause([]);
                  setSelectedProjectIds([]);
                  setMarkAttendance({ status: 'present', notes: '' });
                  fetchData();
                }} 
                className="border-white/10 text-white hover:bg-white/5"
              >
                Skip
              </Button>
              <Button 
                onClick={handlePauseProjects} 
                className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black"
                disabled={selectedProjectIds.length === 0}
                data-testid="pause-projects-btn"
              >
                Pause {selectedProjectIds.length} Project(s)
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Leave Request Dialog */}
      <Dialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
        <DialogContent className="bg-[#121212] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Request Leave</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Leave Balance Info */}
            {leaveBalance && (
              <div className="grid grid-cols-2 gap-2 p-3 bg-[#0A0A0A] rounded border border-white/5">
                <div className="text-center">
                  <p className="text-lg font-semibold text-white">{leaveBalance.leave?.remaining ?? 4}</p>
                  <p className="text-xs text-zinc-500">Leave Left (this month)</p>
                </div>
                <div className="text-center border-l border-white/10">
                  <p className="text-lg font-semibold text-white">∞</p>
                  <p className="text-xs text-zinc-500">Shoot Days</p>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Start Date</Label>
                <input
                  type="date"
                  value={leaveRequest.start_date}
                  onChange={(e) => setLeaveRequest({...leaveRequest, start_date: e.target.value})}
                  className="w-full mt-1 p-2 bg-[#0A0A0A] border border-white/10 rounded text-white"
                  data-testid="leave-start-date"
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">End Date</Label>
                <input
                  type="date"
                  value={leaveRequest.end_date}
                  onChange={(e) => setLeaveRequest({...leaveRequest, end_date: e.target.value})}
                  className="w-full mt-1 p-2 bg-[#0A0A0A] border border-white/10 rounded text-white"
                  data-testid="leave-end-date"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Leave Type</Label>
              <Select value={leaveRequest.leave_type} onValueChange={(v) => setLeaveRequest({...leaveRequest, leave_type: v})}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10">
                  {LEAVE_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value} className="text-white hover:bg-white/10">
                      {t.label} ({t.limit === 'Unlimited' ? 'Unlimited' : `${leaveBalance?.[t.value]?.remaining || 0}/${t.limit} left`})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Reason</Label>
              <Textarea
                value={leaveRequest.reason}
                onChange={(e) => setLeaveRequest({...leaveRequest, reason: e.target.value})}
                className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                placeholder="Why do you need leave?"
                rows={3}
                data-testid="leave-reason"
              />
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowLeaveDialog(false)} className="border-white/10 text-white hover:bg-white/5">
                Cancel
              </Button>
              <Button 
                onClick={handleLeaveRequest} 
                className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black"
                disabled={!leaveRequest.start_date || !leaveRequest.end_date || !leaveRequest.reason}
                data-testid="submit-leave-btn"
              >
                Submit Request
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AttendancePage;
